import { LitElement, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import type { VmToastShowDetail } from 'Components/molecules/vm-toast-container/vm-toast';
import 'Components/molecules/vm-toast-container/vm-toast';
import Styles from './vm-toast-container.scss';

const MAX_TOASTS_DESKTOP = 5;
const MAX_TOASTS_MOBILE = 3;
const DESKTOP_BREAKPOINT = '(min-width: 1024px)';

/**
 * Manages the lifecycle of `<vm-toast>` elements.
 *
 * Place one instance in the page (typically in the base layout). It listens for
 * `vm-toast-show` custom events on `document`, creates a `<vm-toast>` for each,
 * and removes it once its close transition finishes.
 *
 * Renders in the top layer via the Popover API (`popover="manual"`) so toasts stay
 * above any open native `<dialog>` (which is also in the top layer and would
 * otherwise cover a plain z-indexed container). The slot is re-promoted on each new
 * toast and released when the last one closes.
 *
 * @example
 * ```html
 * <!-- In layout template (once per page) -->
 * <vm-toast-container></vm-toast-container>
 *
 * <!-- Trigger from JS anywhere -->
 * <script>
 *   document.dispatchEvent(new CustomEvent('vm-toast-show', {
 *       detail: { message: 'Saved!', variant: 'confirmation' },
 *   }));
 * </script>
 * ```
 */
@customElement('vm-toast-container')
export class VmToastContainer extends LitElement {
    static override styles = Styles;

    override connectedCallback() {
        super.connectedCallback();
        this.classList.add('vm-toast-container');
        this.dataset.testid = 'vm-toast-container';
        // Opt into the top layer via the Popover API. Native `<dialog>` opened with
        // `showModal()` also lives in the top layer, which sits above everything
        // z-indexed; without this, toasts dispatched while a modal is open would
        // render behind the dialog's backdrop.
        if (!this.hasAttribute('popover')) {
            this.setAttribute('popover', 'manual');
        }
        document.addEventListener('vm-toast-show', this._onToastShow as EventListener);
        this.shadowRoot!.addEventListener('vm-toast-close', this._onToastClose);
    }

    override disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('vm-toast-show', this._onToastShow as EventListener);
        this.shadowRoot!.removeEventListener('vm-toast-close', this._onToastClose);
    }

    /** Handle an incoming toast request. If a toast with the same id is already visible, restart its timer instead of creating a duplicate. */
    private _onToastShow = (event: CustomEvent<VmToastShowDetail>) => {
        const { id } = event.detail;

        if (id) {
            const existing = this._getToastById(id);
            if (existing) {
                existing.startTimer();
                return;
            }
        }

        this._enforceMaxToasts();
        this._promoteToTopLayer();

        const toast = document.createElement('vm-toast');
        this.shadowRoot!.querySelector('.vm-toast-container__list')!.appendChild(toast);

        // Wait one frame so the browser registers the initial (hidden) state before transitioning to visible.
        requestAnimationFrame(() => {
            if (!toast.isConnected) return;
            toast.show(event.detail);
        });
    };

    /**
     * Show (or re-show) the container as a popover so it sits in the top layer
     * above any currently-open `<dialog>`. Top-layer ordering is LIFO, so a
     * dialog opened *after* the container was first promoted would cover us —
     * hide-then-show re-stacks the container above everything else.
     */
    private _promoteToTopLayer() {
        if (typeof this.showPopover !== 'function') return; // pre-Popover-API browsers
        try {
            if (this.matches(':popover-open')) {
                this.hidePopover();
            }
            this.showPopover();
        } catch {
            // showPopover() can throw if the element isn't connected or not popover-enabled;
            // we fall back to whatever stacking we have (no worse than before).
        }
    }

    /** FIFO cap: close the oldest active toast(s) to make room for a new one. */
    private _enforceMaxToasts() {
        const max = window.matchMedia(DESKTOP_BREAKPOINT).matches ? MAX_TOASTS_DESKTOP : MAX_TOASTS_MOBILE;
        const active = Array.from(this.shadowRoot!.querySelectorAll('vm-toast')).filter((t) => !t.isClosing);
        const overflow = active.length - max + 1;
        for (let i = 0; i < overflow; i++) {
            const toast = active[i];
            if (!toast) continue;
            if (toast.visible) {
                toast.close();
            } else {
                // Pending toast (show() not yet called) — remove directly since close transition wouldn't fire.
                toast.remove();
            }
        }
    }

    private _getToastById(id: string) {
        const toasts = this.shadowRoot!.querySelectorAll('vm-toast');
        for (const toast of toasts) {
            if (toast.toastId === id && toast.visible) return toast;
        }
        return null;
    }

    /** Remove the toast element from the DOM after its close transition ends. */
    private _onToastClose = (event: Event) => {
        const toast = event.target as HTMLElement;
        toast.remove();
        // No more toasts to display — release our top-layer slot so we don't keep
        // an empty popover open indefinitely.
        if (this.shadowRoot!.querySelectorAll('vm-toast').length === 0) {
            if (typeof this.hidePopover === 'function' && this.matches(':popover-open')) {
                this.hidePopover();
            }
        }
    };

    override render() {
        return html`<div class="vm-toast-container__list" data-testid="vm-toast-container-list"></div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-toast-container': VmToastContainer;
    }
}
